import React from 'react';
import { useQuery } from 'react-query';
import { getBankFromCompany, getBankFromCompanyWiseSettingsId } from '../components/Invoices/InvoicesSingle/services/invoiceSingle.js';

export const useBankAccounts = ({
  wiseProfileId,
  userEmail,
  token,
  wxCompany,
  wiseSettingsId = null,
  enabled = true,
  refetchIntervalMs = 1000
}) => {
  return useQuery(
    ['bankAccounts', wiseProfileId, userEmail, wxCompany, wiseSettingsId],
    async () => {
      if ((!wiseProfileId && !wiseSettingsId) || !userEmail || !token) {
        return [];
      }

      // If wxCompany is "Any", return empty array
      if (wxCompany === "Any") {
        return [];
      }

      try {
        let bankDetails = [];

        if (wiseSettingsId) {
          bankDetails = await getBankFromCompanyWiseSettingsId(
            wiseSettingsId,
            wiseProfileId,
            userEmail,
            token
          );
        } else {
          bankDetails = await getBankFromCompany(
            wiseProfileId,
            userEmail,
            token
          );
        }

        return bankDetails || [];
      } catch (error) {
        console.error('Error fetching bank accounts:', error);
        throw error;
      }
    },
    {
      enabled: enabled && (!!wiseProfileId || !!wiseSettingsId) && !!userEmail && !!token,
      refetchInterval: refetchIntervalMs, // Poll at configured interval
      refetchIntervalInBackground: true, // Continue polling when tab is not active
      staleTime: 10000, // Consider data stale after 10 seconds
      cacheTime: 300000, // Keep in cache for 5 minutes
      retry: 3, // Retry failed requests 3 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      onError: (error) => {
        console.error('Bank accounts query failed:', error);
      },
      // Only refetch if the component is still mounted and visible
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    }
  );
};

export const useBankAccountsWithCurrency = ({
  wiseProfileId,
  userEmail,
  token,
  wxCompany,
  wiseSettingsId = null,
  preferredCurrency = null,
  enabled = true,
  refetchIntervalMs = 1000
}) => {
  const bankQuery = useBankAccounts({
    wiseProfileId,
    userEmail,
    token,
    wxCompany,
    wiseSettingsId,
    enabled,
    refetchIntervalMs
  });

  // Filter bank accounts by preferred currency, fall back to all if no match
  const filteredBankDetails = React.useMemo(() => {
    if (!bankQuery.data) {
      return [];
    }
    if (!preferredCurrency) {
      return bankQuery.data;
    }
    const currencyMatched = bankQuery.data.filter(entry => entry.currency === preferredCurrency);
    return currencyMatched.length > 0 ? currencyMatched : bankQuery.data;
  }, [bankQuery.data, preferredCurrency]);

  return {
    ...bankQuery,
    data: bankQuery.data || [],
    filteredData: filteredBankDetails,
    isLoading: bankQuery.isLoading,
    isError: bankQuery.isError,
    error: bankQuery.error,
    refetch: bankQuery.refetch
  };
};
